import { useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { Pill, showToast } from "../primitives";
import { useAdjustMemoryConfidence } from "../../hooks/use-pattern-actions";
import type { LibraryNode } from "../../../lib/types";

/**
 * Sprint 26.0 — interaction card for a detected *pattern* memory (a feedback
 * memory carrying the pattern marker). Shows the confidence pill, the tasks
 * that evidence it, and the supervisor's quality controls: boost, reduce, and
 * "not a pattern" (demote confidence to 0.3). All go through adjust_memory_confidence.
 */

const TASK_ID_RE = /tsk_[0-9a-f]{6,}/g;

/** First non-frontmatter line of the body, for the pattern summary line. */
function summaryLine(document: string | null): string {
  if (!document) return "(no summary)";
  let body = document;
  if (body.startsWith("---")) {
    const end = body.indexOf("\n---", 3);
    if (end >= 0) body = body.slice(end + 4);
  }
  // The pattern body starts "Pattern detected across tasks …." — show the
  // second meaningful line (the actual summary) when present.
  const lines = body.split("\n").map((l) => l.trim()).filter(Boolean);
  return lines[1] ?? lines[0] ?? "(no summary)";
}

function confidenceMeta(c: number): { variant: "green" | "amber" | "gray"; label: string } {
  if (c >= 0.85) return { variant: "green", label: `${Math.round(c * 100)}% confident` };
  if (c >= 0.6) return { variant: "amber", label: `${Math.round(c * 100)}% confident` };
  return { variant: "gray", label: `${Math.round(c * 100)}% confident` };
}

const cardStyle: CSSProperties = {
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-md)",
  padding: "10px 12px",
  display: "flex",
  flexDirection: "column",
  gap: 8,
  background: "var(--color-background-primary)",
};

const actionStyle: CSSProperties = {
  border: "var(--kuvio-border-default)",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 500,
  padding: "3px 8px",
  borderRadius: "var(--border-radius-sm)",
  fontFamily: "var(--font-sans)",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
};

export function PatternMemoryCard({ memory }: { memory: LibraryNode }) {
  const navigate = useNavigate();
  const adjust = useAdjustMemoryConfidence();
  const [busy, setBusy] = useState(false);

  const confidence = memory.confidence ?? 0.8;
  const meta = confidenceMeta(confidence);
  const taskIds = [...new Set((memory.document ?? "").match(TASK_ID_RE) ?? [])];

  function run(delta: number, label: string) {
    if (busy) return;
    setBusy(true);
    adjust.mutate(
      { memory_id: memory.id, delta, reason: label },
      {
        onSuccess: () => showToast(`${label} — "${memory.name}"`),
        onError: (e) => showToast(e instanceof Error ? e.message : "Adjust failed", "error", true),
        onSettled: () => setBusy(false),
      },
    );
  }

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <Pill variant={meta.variant} icon="repeat">{meta.label}</Pill>
        <button
          onClick={() => navigate(`/library/${memory.id}`)}
          style={{ ...nameStyle, border: "none", background: "transparent", cursor: "pointer", padding: 0, textAlign: "left" }}
          title={`Open ${memory.name}`}
        >
          {memory.name}
        </button>
      </div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{summaryLine(memory.document)}</div>
      {taskIds.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {taskIds.map((id) => (
            <span key={id} style={refChipStyle} title="Supporting task">{id}</span>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <button style={actionStyle} disabled={busy} onClick={() => run(0.1, "Boosted")}>
          <i className="ti ti-arrow-up" style={{ fontSize: 12 }} aria-hidden="true" />Boost
        </button>
        <button style={actionStyle} disabled={busy} onClick={() => run(-0.1, "Reduced")}>
          <i className="ti ti-arrow-down" style={{ fontSize: 12 }} aria-hidden="true" />Reduce
        </button>
        <button
          style={{ ...actionStyle, color: "var(--color-text-danger, var(--color-text-secondary))" }}
          disabled={busy}
          onClick={() => run(0.3 - confidence, "Marked not a pattern")}
        >
          <i className="ti ti-x" style={{ fontSize: 12 }} aria-hidden="true" />Not a pattern
        </button>
      </div>
    </div>
  );
}

const nameStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const refChipStyle: CSSProperties = {
  fontSize: 10,
  fontFamily: "var(--font-mono, monospace)",
  color: "var(--color-text-tertiary)",
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-sm)",
  padding: "1px 5px",
};
